import { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Heading, Paragraph, Skeleton, Tabs } from '@digdir/designsystemet-react';
import { useGetCustomSurveyResultsQuery } from '../../hooks/queries/useGetCustomSurveyResultsQuery';
import { useGetCustomSurveyTemplateByIdQuery } from '../../hooks/queries/useGetCustomSurveyTemplateByIdQuery';
import { getIndividualResponses, IndividualResponse } from '../../api/customSurveys';
import { Routes } from '../../enums/Routes';
import { buildRoute } from '../../utils/routeBuilder';
import { calculateAverageScore } from '../../utils/individualResponsesHelpers';
import { TemplateHeader } from '../CustomSurveyTemplateTrends/components/TemplateHeader';
import { CategoryResults } from '../CustomSurveyTemplateTrends/components/CategoryResults';
import { ResultsGuide } from '../CustomSurveyTemplateTrends/components/ResultsGuide';
import { ResponseDetail } from './components/ResponseDetail';
import { formatDate, fromSurveyResults, groupResults } from '../CustomSurveyTemplateTrends/templateTrendsView';
import trendsClasses from '../CustomSurveyTemplateTrends/CustomSurveyTemplateTrendsPage.module.css';
import classes from './CustomSurveyResultsPage.module.css';

const PAGE_SIZE = 20;

type Tab = 'aggregated' | 'individual';

const canShowResultTabs = (params: {
  hasResults: boolean;
  isHrAdmin: boolean;
  isCompleted: boolean;
  hasInsufficientResponses: boolean;
}): boolean => {
  if (!params.hasResults) return false;
  if (params.isHrAdmin) return true;
  return params.isCompleted && !params.hasInsufficientResponses;
};

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

const ResponseRow = ({
  response,
  number,
  totalQuestions,
  detailPath,
  returnPage,
}: {
  response: IndividualResponse;
  number: number;
  totalQuestions: number;
  detailPath: string;
  returnPage: number;
}) => {
  const { t, i18n } = useTranslation();
  const average = calculateAverageScore(response);
  const label =
    response.isAnonymous || !response.respondentEmail
      ? t('customSurveys.surveyResults.individual.response', { number })
      : response.respondentEmail;
  const submitted = new Date(response.submittedAt).toLocaleString(locale(i18n.language), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <li className={classes.responseRow}>
      <span aria-hidden="true" className={classes.responseNumber}>
        {number}
      </span>
      <div className={classes.responseText}>
        <Heading level={3} data-size="2xs">
          {label}
        </Heading>
        <span className={trendsClasses.subtle}>
          {t('customSurveys.surveyResults.individual.meta', {
            submitted,
            answered: response.answers.length,
            total: totalQuestions,
          })}
        </span>
      </div>
      {average && (
        <div className={classes.responseScore}>
          <span className={trendsClasses.subtle}>{t('customSurveys.surveyResults.individual.average')}</span>
          <span className={classes.responseScoreValue}>
            {average.score.toLocaleString(locale(i18n.language), { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
          </span>
        </div>
      )}
      <Button asChild variant="secondary" data-size="sm">
        <Link
          to={detailPath}
          state={{ returnPage }}
          aria-label={t('customSurveys.surveyResults.individual.openLabel', { name: label })}
        >
          {t('customSurveys.surveyResults.individual.open')}
        </Link>
      </Button>
    </li>
  );
};

export const CustomSurveyResultsPage = () => {
  const { t, i18n } = useTranslation();
  const { surveyId, templateId, responseId } = useParams<{
    surveyId: string;
    templateId: string;
    responseId?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const returnPage = (location.state as { returnPage?: number } | null)?.returnPage;

  const [activeTab, setActiveTab] = useState<Tab>(returnPage || responseId ? 'individual' : 'aggregated');
  const [currentPage, setCurrentPage] = useState(returnPage || 1);

  // Ett svar vises under «Hvert svar for seg», og tilbake fra ett svar vises listen igjen
  useEffect(() => {
    if (returnPage || responseId) setActiveTab('individual');
  }, [returnPage, responseId]);

  const { data: results, isLoading, error } = useGetCustomSurveyResultsQuery(Number(surveyId));
  const { data: template } = useGetCustomSurveyTemplateByIdQuery(templateId ? Number(templateId) : null);

  const {
    data: individual,
    isLoading: isLoadingIndividual,
    error: individualError,
  } = useQuery({
    queryKey: ['individualResponses', surveyId, currentPage],
    queryFn: () => getIndividualResponses(Number(surveyId), PAGE_SIZE, (currentPage - 1) * PAGE_SIZE),
    enabled: activeTab === 'individual' && !responseId && Boolean(results?.viewerIsHrAdmin || results?.isCompleted),
  });

  const page = (content: ReactNode) => (
    <div className={trendsClasses.page}>
      <TemplateHeader
        templateId={templateId ?? ''}
        title={template?.name ?? results?.title}
        surveyId={Number(surveyId)}
      />
      {content}
    </div>
  );

  if (isLoading) {
    return page(
      <div aria-busy="true" className={trendsClasses.loading}>
        <span className="ds-sr-only">{t('customSurveys.surveyResults.loading')}</span>
        <Skeleton variant="rectangle" width="100%" height={120} />
        <Skeleton variant="rectangle" width="100%" height={360} />
      </div>,
    );
  }

  if (error || !results) {
    const key = error ? 'error' : 'notFound';
    return page(
      <Alert data-color={error ? 'danger' : 'warning'}>
        <Heading level={3} data-size="2xs">
          {t(`customSurveys.surveyResults.${key}.title`)}
        </Heading>
        <Paragraph>{t(`customSurveys.surveyResults.${key}.message`)}</Paragraph>
      </Alert>,
    );
  }

  const hasInsufficientResponses = results.isCompleted && results.responseRate < results.minimumResponseRate;
  const showTabs = canShowResultTabs({
    hasResults: results.questionStatistics.length > 0,
    isHrAdmin: results.viewerIsHrAdmin,
    isCompleted: results.isCompleted,
    hasInsufficientResponses,
  });
  const isLockedWhileActive = results.isActive && !results.viewerIsHrAdmin;
  const isLockedByThreshold = hasInsufficientResponses && !results.viewerIsHrAdmin;

  const deadline =
    results.activatedAt && results.deadlineInDays
      ? new Date(new Date(results.activatedAt).getTime() + results.deadlineInDays * 24 * 60 * 60 * 1000).toISOString()
      : undefined;

  const aggregated =
    results.questionStatistics.length === 0 ? (
      <Alert data-color="info" className={trendsClasses.notice}>
        <Heading level={3} data-size="2xs">
          {t('customSurveys.surveyResults.noAnswers.title')}
        </Heading>
        <Paragraph data-size="sm">{t('customSurveys.surveyResults.noAnswers.message')}</Paragraph>
      </Alert>
    ) : (
      <section aria-labelledby="survey-results-heading" className={trendsClasses.results}>
        <div className={trendsClasses.resultsIntro}>
          <Heading level={2} data-size="sm" id="survey-results-heading">
            {t('customSurveys.templateTrends.results.title')}
          </Heading>
          <Paragraph data-size="sm" className={trendsClasses.subtle}>
            {t('customSurveys.surveyResults.aggregatedIntro')}
          </Paragraph>
          <ResultsGuide view="survey" />
        </div>
        {groupResults(fromSurveyResults(results)).map((group) => (
          <CategoryResults key={group.category ?? 'other'} group={group} surveyId={results.id} />
        ))}
      </section>
    );

  const totalPages = individual ? Math.ceil(individual.totalCount / PAGE_SIZE) : 0;
  const allAnonymous = individual?.responses.every((response) => response.isAnonymous || !response.respondentEmail);

  const individualContent = isLoadingIndividual ? (
    <Skeleton variant="rectangle" width="100%" height={240} />
  ) : individualError ? (
    <Alert data-color="info" className={trendsClasses.notice}>
      <Heading level={3} data-size="2xs">
        {t('customSurveys.surveyResults.individual.lockedTitle')}
      </Heading>
      <Paragraph data-size="sm">
        {t('customSurveys.surveyResults.individual.lockedMessage', { rate: Math.round(results.minimumResponseRate) })}
      </Paragraph>
    </Alert>
  ) : individual ? (
    <section aria-labelledby="survey-individual-heading" className={classes.individual}>
      <div className={classes.individualIntro}>
        <Heading level={2} data-size="sm" id="survey-individual-heading">
          {t('customSurveys.surveyResults.individual.title')}
        </Heading>
        <Paragraph data-size="sm" className={trendsClasses.subtle}>
          {t('customSurveys.surveyResults.individual.count', { count: individual.totalCount })}
          {allAnonymous && ` ${t('customSurveys.surveyResults.individual.noNames')}`}
        </Paragraph>
      </div>
      {individual.responses.length > 0 && (
        <ol className={classes.responseList}>
          {individual.responses.map((response, index) => (
            <ResponseRow
              key={response.id}
              response={response}
              number={(currentPage - 1) * PAGE_SIZE + index + 1}
              totalQuestions={results.questionStatistics.length}
              returnPage={currentPage}
              detailPath={buildRoute(Routes.DashboardCustomSurveyResponseDetail, {
                templateId: templateId ?? '',
                surveyId: results.id,
                responseId: response.id,
              })}
            />
          ))}
        </ol>
      )}
      {totalPages > 1 && (
        <nav aria-label={t('customSurveys.surveyResults.individual.pagination')} className={classes.pagination}>
          {/* Knappene skjules i stedet for å deaktiveres når det ikke finnes en side å gå til */}
          {currentPage > 1 && (
            <Button variant="secondary" data-size="sm" onClick={() => setCurrentPage((current) => current - 1)}>
              {t('customSurveys.surveyResults.individual.previous')}
            </Button>
          )}
          <span>{t('customSurveys.surveyResults.individual.pageInfo', { current: currentPage, total: totalPages })}</span>
          {currentPage < totalPages && (
            <Button variant="secondary" data-size="sm" onClick={() => setCurrentPage((current) => current + 1)}>
              {t('customSurveys.surveyResults.individual.next')}
            </Button>
          )}
        </nav>
      )}
    </section>
  ) : null;

  return page(
    <>
      <Alert data-color="accent" className={trendsClasses.notice}>
        <Heading level={3} data-size="2xs">
          {t('customSurveys.surveyResults.intro.title')}
        </Heading>
        {results.activatedAt && (
          <Paragraph data-size="sm">
            {t('customSurveys.surveyResults.intro.text', { date: formatDate(results.activatedAt, i18n.language, 'long') })}
          </Paragraph>
        )}
      </Alert>

      <dl className={trendsClasses.stats}>
        <div>
          <dt>{t('customSurveys.surveyResults.stats.responseRate')}</dt>
          <dd>
            {t('customSurveys.surveyResults.stats.percent', { value: Math.round(results.responseRate) })}
            <span className={classes.statNote}>
              {t('customSurveys.surveyResults.stats.responded', {
                count: results.actualResponseCount,
                total: results.expectedParticipantsCount,
              })}
            </span>
          </dd>
        </div>
        {results.activatedAt && (
          <div>
            <dt>{t('customSurveys.surveyResults.stats.sent')}</dt>
            <dd>{formatDate(results.activatedAt, i18n.language)}</dd>
          </div>
        )}
        {deadline && (
          <div>
            <dt>{t('customSurveys.surveyResults.stats.deadline')}</dt>
            <dd>{formatDate(deadline, i18n.language)}</dd>
          </div>
        )}
      </dl>

      {isLockedWhileActive || isLockedByThreshold ? (
        <Alert data-color={isLockedWhileActive ? 'info' : 'warning'} className={trendsClasses.notice}>
          <Heading level={3} data-size="2xs">
            {t(`customSurveys.surveyResults.${isLockedWhileActive ? 'active' : 'insufficient'}.title`)}
          </Heading>
          <Paragraph data-size="sm">
            {t(`customSurveys.surveyResults.${isLockedWhileActive ? 'active' : 'insufficient'}.message`, {
              count: results.actualResponseCount,
              total: results.expectedParticipantsCount,
              rate: Math.round(results.minimumResponseRate),
            })}
          </Paragraph>
        </Alert>
      ) : showTabs ? (
        <Tabs
          value={activeTab}
          onChange={(value) => {
            setActiveTab(value as Tab);
            setCurrentPage(1);
            // Fra ett svar går fanene tilbake til undersøkelsen
            if (responseId) {
              navigate(buildRoute(Routes.DashboardCustomSurveySurvey, { templateId: templateId ?? '', surveyId: results.id }), {
                state: value === 'individual' ? { returnPage: returnPage ?? 1 } : undefined,
              });
            }
          }}
          className={classes.tabs}
        >
          <Tabs.List>
            <Tabs.Tab value="aggregated">
              {t('customSurveys.surveyResults.tabs.aggregated')}
              <span className={classes.tabCount}>{results.questionStatistics.length}</span>
            </Tabs.Tab>
            <Tabs.Tab value="individual">
              {t('customSurveys.surveyResults.tabs.individual')}
              <span className={classes.tabCount}>{results.actualResponseCount}</span>
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="aggregated">{aggregated}</Tabs.Panel>
          <Tabs.Panel value="individual">
            {responseId ? (
              <ResponseDetail
                templateId={templateId ?? ''}
                surveyId={results.id}
                responseId={Number(responseId)}
                returnPage={returnPage}
              />
            ) : (
              individualContent
            )}
          </Tabs.Panel>
        </Tabs>
      ) : (
        aggregated
      )}
    </>,
  );
};
